// =======================================================
// Horizon — stacked slow-breathing horizontal lines
// =======================================================
//
// Custom-made for ultrawide: horizontal lines that wave up & down like a
// calm seismograph. Staggered phases across lines make a breathing band.

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uLoopPhase;
  uniform vec2  uResolution;
  uniform vec3  uColor;
  uniform vec3  uBg;
  uniform float uCount;
  uniform float uSpeed;
  uniform float uAmp;
  uniform float uThickness;
  uniform float uStagger;
  uniform float uWaveFreq;

  ${GLSL_COMMON}

  void main() {
    vec2 frag = vUv * uResolution;
    float h = uResolution.y;
    float total = 0.0;

    // for each line, evaluate its wavy y and shade if we're close
    for (float i = 0.0; i < 80.0; i += 1.0) {
      if (i >= uCount) break;
      float baseY = (i + 0.5) / uCount * h;
      // bobbing offset: individual speed+phase per line
      float phaseA = (uLoopPhase * max(1.0, floor(uSpeed * 2.0 + 0.5)) + i * uStagger / 6.2831) * 6.2831;
      float off = sin(phaseA) * h * uAmp;
      // horizontal ripple along the line
      float phaseB = frag.x * uWaveFreq / h * 6.2831 + (uLoopPhase * max(1.0, floor(uSpeed + 0.5)) + i * 0.1432) * 6.2831;
      float ripple = sin(phaseB) * h * uAmp * 0.35;
      float y = baseY + off + ripple;
      float d = abs(frag.y - y);
      float line = smoothstep(uThickness + 0.5, uThickness - 0.5, d);
      total = max(total, line);
    }

    vec3 col = mix(uBg, uColor, clamp(total, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class Horizon extends Generator {
  constructor() {
    super({
      name: 'Horizon Lines',
      category: 'ambient',
      schema: {
        count:     { default: 14,  min: 1,   max: 80,  step: 1,   label: 'Line count' },
        speed:     { default: 0.8, min: 0,   max: 6,   step: 0.05,label: 'Breath speed' },
        amp:       { default: 0.08,min: 0,   max: 0.45,step: 0.005,label: 'Amplitude' },
        thickness: { default: 1.8, min: 0.5, max: 10,  step: 0.1, label: 'Thickness (px)' },
        stagger:   { default: 0.7, min: 0,   max: 3,   step: 0.01,label: 'Phase stagger' },
        waveFreq:  { default: 1.5, min: 0.2, max: 8,   step: 0.05,label: 'Wave frequency' },
        accent:    { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uLoopPhase:  { value: 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColor:      { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color('#000000') },
        uCount:      { value: this.v('count') },
        uSpeed:      { value: this.v('speed') },
        uAmp:        { value: this.v('amp') },
        uThickness:  { value: this.v('thickness') },
        uStagger:    { value: this.v('stagger') },
        uWaveFreq:   { value: this.v('waveFreq') },
      },
      transparent: false, depthWrite: false, depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent') u.uColor.value.set(val);
    else if (`u${key[0].toUpperCase()}${key.slice(1)}` in u) {
      u[`u${key[0].toUpperCase()}${key.slice(1)}`].value = val;
    }
  }

  update(dt, time, engine) {
    if (this.mesh) this.mesh.material.uniforms.uLoopPhase.value = engine.getLoopInfo(time).phase;
  }
}
